import { Button, Stack, Typography } from '@mui/material';
import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import { HomeHQPaywall, PaywallCard } from '@/components/billing/HomeHQPaywall';
import { formatPrice } from '@/components/billing/paywallCopy';
import { PlanPicker } from '@/components/billing/PlanPicker';
import {
  useHomeHQAccess,
  useHomeHQPlans,
  useOpenHomeHQBillingPortal,
} from '@/hooks/useHomeHQAccess';

const SUBSCRIBED_STATUSES = new Set(['active', 'trialing', 'canceling', 'past_due']);

const formatDate = (epochSeconds: number | null) =>
  epochSeconds ? new Date(epochSeconds * 1000).toLocaleDateString() : null;

/**
 * /dashboard/plans — choose a plan, or manage an existing subscription.
 * Linked from Klaviyo emails (`?manage=1` opens the Stripe billing portal)
 * and used as Stripe Checkout's cancel URL and the portal's return URL.
 */
export const PlansView = () => {
  const [searchParams] = useSearchParams();
  const { data: access, isLoading } = useHomeHQAccess();
  const { data: plans } = useHomeHQPlans();
  const portal = useOpenHomeHQBillingPortal();
  const autoOpened = useRef(false);

  const wantsManage = searchParams.get('manage') === '1';
  useEffect(() => {
    if (wantsManage && access?.canManageBilling && !autoOpened.current) {
      autoOpened.current = true;
      portal.mutate();
    }
  }, [wantsManage, access?.canManageBilling]);

  if (isLoading || !access) return null;

  // Lapsed (including household members whose owner lapsed): same card as the paywall.
  if (!access.hasAccess) return <HomeHQPaywall access={access} />;

  if (access.householdOwnerId) {
    return (
      <PaywallCard>
        <Typography component="h1" sx={{ fontSize: 20, fontWeight: 600 }} textAlign="center">
          Your Home HQ plan
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          Your household&apos;s subscription is managed by the account owner.
        </Typography>
      </PaywallCard>
    );
  }

  const isSubscribed = access.canManageBilling && SUBSCRIBED_STATUSES.has(access.status);
  const current = plans?.find((p) => p.plan === access.plan && p.interval === access.interval);
  const detail = access.cancelAtPeriodEnd
    ? `Cancels on ${formatDate(access.currentPeriodEnd)}.`
    : access.promoEndsAt
    ? `Free until ${formatDate(access.promoEndsAt)}.${
        access.hasPaymentMethod ? '' : ' Add a payment method to continue after that.'
      }`
    : `Renews on ${formatDate(access.currentPeriodEnd)}.`;

  return (
    <PaywallCard>
      <Stack spacing={1.5} sx={{ textAlign: 'center', width: '100%' }}>
        <Typography component="h1" sx={{ fontSize: 20, fontWeight: 600 }}>
          {isSubscribed ? 'Your Home HQ plan' : 'Choose your Home HQ plan'}
        </Typography>
        {searchParams.get('checkout') === 'canceled' && (
          <Typography variant="body2" color="text.secondary">
            Checkout was canceled. You haven&apos;t been charged.
          </Typography>
        )}
        {!isSubscribed && access.status === 'granted' && access.grantExpiresAt && (
          <Typography variant="body2" color="text.secondary">
            Your included access runs until {formatDate(access.grantExpiresAt)}. Subscribe now to
            keep it going without interruption.
          </Typography>
        )}
      </Stack>

      {isSubscribed ? (
        <Stack spacing={2} sx={{ width: '100%', textAlign: 'center' }}>
          <Stack spacing={0.5}>
            <Typography sx={{ fontWeight: 600 }}>
              {current?.name ?? 'HomeHQ'}
              {current &&
                ` · ${formatPrice(current.unitAmount, current.currency)} / ${
                  current.interval === 'month' ? 'mo' : 'yr'
                }`}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {detail}
            </Typography>
          </Stack>
          <Button fullWidth disabled={portal.isPending} onClick={() => portal.mutate()}>
            {portal.isPending ? 'Opening…' : 'Manage subscription'}
          </Button>
          {portal.isError && (
            <Typography role="alert" variant="caption" color="error">
              Could not open billing. Please try again.
            </Typography>
          )}
        </Stack>
      ) : (
        <Stack sx={{ width: '100%' }}>
          <PlanPicker />
        </Stack>
      )}
    </PaywallCard>
  );
};
